import { z } from 'zod';
import { isoDateSchema, sexSchema } from './people.js';
import { phoneSchema, emailSchema } from './auth.js';
import { slugSchema } from './tenancy.js';

/**
 * Ministério Infantil (M19). O módulo mais sensível do produto: ele lida com
 * segurança física de menores e com dado de saúde.
 *
 * Regra que atravessa tudo: **nada de saúde aparece em tela pública**, e todo
 * acesso à ficha é registrado.
 */

// ── Salas ────────────────────────────────────────────────────────────────────

export const createClassroomSchema = z.object({
  name: z.string().trim().min(2, 'Dê um nome à sala').max(120),
  slug: slugSchema.optional(),
  description: z.string().trim().max(500).nullish(),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Escolha uma cor')
    .optional(),
  minAge: z.coerce.number().int().min(0).max(18),
  maxAge: z.coerce.number().int().min(0).max(18),
  /** Quantas crianças por adulto esta sala aceita. */
  childrenPerAdult: z.coerce.number().int().min(1).max(30).default(6),
  capacity: z.coerce.number().int().min(0).max(500).default(0),
  congregationId: z.string().uuid().nullish(),
  active: z.boolean().default(true),
});
export type CreateClassroomInput = z.infer<typeof createClassroomSchema>;

export const updateClassroomSchema = createClassroomSchema.partial();
export type UpdateClassroomInput = z.infer<typeof updateClassroomSchema>;

export const classroomSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  color: z.string(),
  minAge: z.number().int(),
  maxAge: z.number().int(),
  childrenPerAdult: z.number().int(),
  capacity: z.number().int(),
  congregationId: z.string().uuid().nullable(),
  congregationName: z.string().nullable(),
  active: z.boolean(),
  /** Quantas crianças a faixa etária desta sala tem hoje. */
  childrenCount: z.number().int(),
});
export type Classroom = z.infer<typeof classroomSchema>;

/** As salas que quase toda igreja tem, prontas no primeiro dia. */
export const CLASSROOM_PRESETS = [
  { name: 'Berçário', minAge: 0, maxAge: 2, childrenPerAdult: 3, color: '#F472B6' },
  { name: 'Maternal', minAge: 3, maxAge: 4, childrenPerAdult: 6, color: '#FB923C' },
  { name: 'Jardim', minAge: 5, maxAge: 6, childrenPerAdult: 8, color: '#FACC15' },
  { name: 'Juniores', minAge: 7, maxAge: 10, childrenPerAdult: 10, color: '#4ADE80' },
  { name: 'Pré-adolescentes', minAge: 11, maxAge: 12, childrenPerAdult: 12, color: '#60A5FA' },
] as const;

// ── Criança ──────────────────────────────────────────────────────────────────

export const CHILD_STATUS = ['ACTIVE', 'INACTIVE', 'VISITOR'] as const;
export const childStatusSchema = z.enum(CHILD_STATUS);
export type ChildStatus = z.infer<typeof childStatusSchema>;

export const CHILD_STATUS_LABEL: Record<ChildStatus, string> = {
  ACTIVE: 'Frequenta',
  INACTIVE: 'Afastada',
  VISITOR: 'Visitante',
};

export const GUARDIAN_RELATIONSHIPS = [
  'MOTHER',
  'FATHER',
  'GRANDPARENT',
  'UNCLE_AUNT',
  'SIBLING',
  'LEGAL_GUARDIAN',
  'OTHER',
] as const;
export const guardianRelationshipSchema = z.enum(GUARDIAN_RELATIONSHIPS);
export type GuardianRelationship = z.infer<typeof guardianRelationshipSchema>;

export const GUARDIAN_RELATIONSHIP_LABEL: Record<GuardianRelationship, string> = {
  MOTHER: 'Mãe',
  FATHER: 'Pai',
  GRANDPARENT: 'Avô ou avó',
  UNCLE_AUNT: 'Tio ou tia',
  SIBLING: 'Irmão ou irmã',
  LEGAL_GUARDIAN: 'Responsável legal',
  OTHER: 'Outro',
};

export const guardianInputSchema = z.object({
  name: z.string().trim().min(3, 'Informe o nome do responsável').max(160),
  relationship: guardianRelationshipSchema.default('OTHER'),
  phone: phoneSchema.nullish(),
  email: emailSchema.nullish(),
  isPrimary: z.boolean().default(false),
  canPickup: z.boolean().default(true),
  receivesNotifications: z.boolean().default(true),
  /** Quando o responsável também é membro da igreja. */
  personId: z.string().uuid().nullish(),
});
export type GuardianInput = z.infer<typeof guardianInputSchema>;

export const createChildSchema = z
  .object({
    fullName: z
      .string()
      .trim()
      .min(3, 'Informe o nome completo da criança')
      .max(160)
      .refine((valor) => valor.includes(' '), 'Informe nome e sobrenome'),
    nickname: z.string().trim().max(60).nullish(),
    birthDate: isoDateSchema,
    sex: sexSchema.default('UNDISCLOSED'),
    status: childStatusSchema.default('ACTIVE'),
    congregationId: z.string().uuid().nullish(),
    notes: z.string().trim().max(1000).nullish(),
    /**
     * Criança **precisa** de responsável (LGPD art. 14). Um, no mínimo, e um
     * deles é o principal.
     */
    guardians: z.array(guardianInputSchema).min(1, 'Toda criança precisa de um responsável').max(6),
  })
  .refine((dados) => dados.guardians.some((guardian) => guardian.isPrimary), {
    path: ['guardians'],
    message: 'Marque quem é o responsável principal',
  });
export type CreateChildInput = z.infer<typeof createChildSchema>;

export const updateChildSchema = z.object({
  fullName: z.string().trim().min(3).max(160).optional(),
  nickname: z.string().trim().max(60).nullish(),
  birthDate: isoDateSchema.optional(),
  sex: sexSchema.optional(),
  status: childStatusSchema.optional(),
  congregationId: z.string().uuid().nullish(),
  notes: z.string().trim().max(1000).nullish(),
  photoUrl: z.string().trim().max(500).nullish(),
});
export type UpdateChildInput = z.infer<typeof updateChildSchema>;

export const guardianSchema = guardianInputSchema.extend({
  id: z.string().uuid(),
  personId: z.string().uuid().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
});
export type Guardian = z.infer<typeof guardianSchema>;

/** O selo que aparece no cartão, na etiqueta e na tela da sala. */
export const HEALTH_ALERT_SEVERITIES = ['INFO', 'ATTENTION', 'SEVERE'] as const;
export const healthAlertSeveritySchema = z.enum(HEALTH_ALERT_SEVERITIES);
export type HealthAlertSeverity = z.infer<typeof healthAlertSeveritySchema>;

export const HEALTH_ALERT_SEVERITY_LABEL: Record<HealthAlertSeverity, string> = {
  INFO: 'Informação',
  ATTENTION: 'Atenção',
  SEVERE: 'Grave',
};

export const healthAlertSchema = z.object({
  severity: healthAlertSeveritySchema,
  /** Curto de propósito: cabe na etiqueta e é lido de relance. */
  label: z.string(),
});
export type HealthAlert = z.infer<typeof healthAlertSchema>;

export const childSchema = z.object({
  id: z.string().uuid(),
  fullName: z.string(),
  nickname: z.string().nullable(),
  birthDate: z.string(),
  /** Calculada, nunca digitada. */
  age: z.number().int(),
  sex: sexSchema,
  status: childStatusSchema,
  photoUrl: z.string().nullable(),
  notes: z.string().nullable(),
  congregationId: z.string().uuid().nullable(),
  congregationName: z.string().nullable(),
  /** A sala sai da idade — e muda sozinha no aniversário. */
  classroomId: z.string().uuid().nullable(),
  classroomName: z.string().nullable(),
  guardians: z.array(guardianSchema),
  /** Vazio para quem não pode ver saúde: o selo é dado de saúde. */
  healthAlerts: z.array(healthAlertSchema),
  /** A liderança do kids vê que há restrição legal; o detalhe fica na ficha. */
  hasRestriction: z.boolean(),
  consents: z.record(z.string(), z.boolean()),
});
export type Child = z.infer<typeof childSchema>;

export const childrenQuerySchema = z.object({
  search: z.string().trim().max(120).optional(),
  classroomId: z.string().uuid().optional(),
  status: childStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ChildrenQuery = z.infer<typeof childrenQuerySchema>;

// ── Autorizados a retirar ────────────────────────────────────────────────────

export const createPickupSchema = z.object({
  name: z.string().trim().min(3, 'Informe o nome de quem pode retirar').max(160),
  relationship: guardianRelationshipSchema.default('OTHER'),
  phone: phoneSchema.nullish(),
  /** Documento conferido na porta. Guardado cifrado. */
  document: z.string().trim().max(40).nullish(),
  notes: z.string().trim().max(500).nullish(),
  photoUrl: z.string().trim().max(500).nullish(),
});
export type CreatePickupInput = z.infer<typeof createPickupSchema>;

export const pickupSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  relationship: guardianRelationshipSchema,
  phone: z.string().nullable(),
  /** Só os últimos dígitos: a conferência é visual, não é consulta de cadastro. */
  documentHint: z.string().nullable(),
  notes: z.string().nullable(),
  photoUrl: z.string().nullable(),
  active: z.boolean(),
});
export type Pickup = z.infer<typeof pickupSchema>;

// ── Restrições legais ────────────────────────────────────────────────────────

export const RESTRICTION_KINDS = [
  'SHARED_CUSTODY',
  'PROTECTIVE_ORDER',
  'FORBIDDEN_APPROACH',
  'OTHER',
] as const;
export const restrictionKindSchema = z.enum(RESTRICTION_KINDS);
export type RestrictionKind = z.infer<typeof restrictionKindSchema>;

export const RESTRICTION_KIND_LABEL: Record<RestrictionKind, string> = {
  SHARED_CUSTODY: 'Guarda compartilhada',
  PROTECTIVE_ORDER: 'Medida protetiva',
  FORBIDDEN_APPROACH: 'Pessoa proibida de aproximação',
  OTHER: 'Outra restrição',
};

export const createRestrictionSchema = z.object({
  kind: restrictionKindSchema.default('OTHER'),
  details: z.string().trim().min(5, 'Descreva a restrição').max(2000),
});
export type CreateRestrictionInput = z.infer<typeof createRestrictionSchema>;

export const restrictionSchema = z.object({
  id: z.string().uuid(),
  kind: restrictionKindSchema,
  details: z.string(),
  active: z.boolean(),
  createdAt: z.string().datetime(),
});
export type Restriction = z.infer<typeof restrictionSchema>;

// ── Ficha de saúde ───────────────────────────────────────────────────────────

export const ALLERGY_SEVERITIES = ['MILD', 'MODERATE', 'SEVERE'] as const;
export const allergySeveritySchema = z.enum(ALLERGY_SEVERITIES);
export type AllergySeverity = z.infer<typeof allergySeveritySchema>;

export const ALLERGY_SEVERITY_LABEL: Record<AllergySeverity, string> = {
  MILD: 'Leve',
  MODERATE: 'Moderada',
  SEVERE: 'Grave (anafilaxia)',
};

export const allergySchema = z.object({
  name: z.string().trim().min(2, 'Informe a alergia').max(120),
  severity: allergySeveritySchema.default('MILD'),
  /** O que fazer se a reação acontecer. */
  action: z.string().trim().max(500).nullish(),
});

export const medicationSchema = z.object({
  name: z.string().trim().min(2, 'Informe o medicamento').max(120),
  dose: z.string().trim().max(120).nullish(),
  /** Quando administrar: horário fixo, ou em que situação. */
  when: z.string().trim().max(300).nullish(),
  continuous: z.boolean().default(false),
});

export const conditionSchema = z.object({
  name: z.string().trim().min(2, 'Informe a condição').max(120),
  /** Orientação prática ao professor, na língua dele. */
  guidance: z.string().trim().max(500).nullish(),
});

export const healthProfileSchema = z.object({
  allergies: z.array(allergySchema).max(20).default([]),
  medications: z.array(medicationSchema).max(20).default([]),
  conditions: z.array(conditionSchema).max(20).default([]),
  /** Intolerâncias, dieta, se pode receber o lanche da igreja. */
  dietaryNotes: z.string().trim().max(1000).nullish(),
  canEatChurchSnack: z.boolean().default(true),
  accessibilityNotes: z.string().trim().max(1000).nullish(),
  emergencyContactName: z.string().trim().max(160).nullish(),
  emergencyContactPhone: phoneSchema.nullish(),
  preferredHospital: z.string().trim().max(200).nullish(),
  healthPlan: z.string().trim().max(200).nullish(),
  /** Autorização para atendimento de urgência sem o responsável presente. */
  emergencyCareAuthorized: z.boolean().default(false),
  generalNotes: z.string().trim().max(2000).nullish(),
});
export type HealthProfileInput = z.infer<typeof healthProfileSchema>;

export const healthProfileViewSchema = healthProfileSchema.extend({
  childId: z.string().uuid(),
  reviewedAt: z.string().datetime(),
  /** Ficha sem revisão há mais de 12 meses pede conferência no check-in. */
  needsReview: z.boolean(),
  alerts: z.array(healthAlertSchema),
});
export type HealthProfileView = z.infer<typeof healthProfileViewSchema>;

/** Quantos meses uma ficha vale antes de pedir revisão. */
export const MESES_ATE_REVISAR_FICHA = 12;

// ── Consentimentos ───────────────────────────────────────────────────────────

export const CONSENT_KINDS = ['IMAGE', 'MEDICATION', 'DATA_PROCESSING', 'EMERGENCY_CARE'] as const;
export const consentKindSchema = z.enum(CONSENT_KINDS);
export type ConsentKind = z.infer<typeof consentKindSchema>;

export const CONSENT_KIND_LABEL: Record<ConsentKind, string> = {
  IMAGE: 'Uso de imagem',
  MEDICATION: 'Administrar medicamento',
  DATA_PROCESSING: 'Tratamento dos dados da criança',
  EMERGENCY_CARE: 'Atendimento de urgência',
};

export const CONSENT_KIND_AJUDA: Record<ConsentKind, string> = {
  IMAGE: 'Foto da criança no cadastro e em registros do culto.',
  MEDICATION: 'Sem isto, nenhum voluntário administra remédio nenhum.',
  DATA_PROCESSING: 'Guardar nome, data de nascimento e ficha de saúde (LGPD art. 14).',
  EMERGENCY_CARE: 'Levar ao pronto-socorro se o responsável não estiver presente.',
};

export const setConsentSchema = z.object({
  kind: consentKindSchema,
  granted: z.boolean(),
  guardianId: z.string().uuid().nullish(),
  /** Como a igreja registrou: assinatura no papel, no app, presencialmente. */
  evidence: z.string().trim().max(300).nullish(),
});
export type SetConsentInput = z.infer<typeof setConsentSchema>;

export const consentSchema = z.object({
  kind: consentKindSchema,
  granted: z.boolean(),
  guardianId: z.string().uuid().nullable(),
  guardianName: z.string().nullable(),
  evidence: z.string().nullable(),
  grantedAt: z.string().datetime().nullable(),
  revokedAt: z.string().datetime().nullable(),
});
export type Consent = z.infer<typeof consentSchema>;

// ── Habilitação de quem serve no infantil ────────────────────────────────────

export const CLEARANCE_KINDS = [
  'SIGNED_DECLARATION',
  'INTERVIEW',
  'CHILD_PROTECTION_COURSE',
  'BACKGROUND_CHECK',
  'OTHER',
] as const;
export const clearanceKindSchema = z.enum(CLEARANCE_KINDS);
export type ClearanceKind = z.infer<typeof clearanceKindSchema>;

export const CLEARANCE_KIND_LABEL: Record<ClearanceKind, string> = {
  SIGNED_DECLARATION: 'Declaração assinada',
  INTERVIEW: 'Entrevista',
  CHILD_PROTECTION_COURSE: 'Curso de proteção à criança',
  BACKGROUND_CHECK: 'Verificação de antecedentes',
  OTHER: 'Outro',
};

export const createClearanceSchema = z.object({
  personId: z.string().uuid('Escolha a pessoa'),
  kind: clearanceKindSchema,
  issuedAt: isoDateSchema,
  validUntil: isoDateSchema.nullish(),
  notes: z.string().trim().max(500).nullish(),
  fileId: z.string().uuid().nullish(),
});
export type CreateClearanceInput = z.infer<typeof createClearanceSchema>;

export const clearanceSchema = z.object({
  id: z.string().uuid(),
  personId: z.string().uuid(),
  personName: z.string(),
  kind: clearanceKindSchema,
  issuedAt: z.string(),
  validUntil: z.string().nullable(),
  expired: z.boolean(),
  /** Vence em menos de 60 dias: a igreja precisa renovar antes do domingo. */
  expiringSoon: z.boolean(),
  notes: z.string().nullable(),
  fileId: z.string().uuid().nullable(),
});
export type Clearance = z.infer<typeof clearanceSchema>;

/** Quantos dias antes do vencimento a habilitação começa a avisar. */
export const DIAS_DE_AVISO_DE_VENCIMENTO = 60;

// ── Log de acesso à ficha de saúde ───────────────────────────────────────────

export const HEALTH_ACCESS_KINDS = ['ALERTS', 'PROFILE', 'EXPORT'] as const;
export const healthAccessKindSchema = z.enum(HEALTH_ACCESS_KINDS);
export type HealthAccessKind = z.infer<typeof healthAccessKindSchema>;

export const HEALTH_ACCESS_KIND_LABEL: Record<HealthAccessKind, string> = {
  ALERTS: 'Viu os alertas',
  PROFILE: 'Abriu a ficha',
  EXPORT: 'Exportou a ficha',
};

export const healthAccessLogSchema = z.object({
  id: z.string().uuid(),
  childId: z.string().uuid(),
  childName: z.string(),
  userId: z.string().uuid().nullable(),
  userName: z.string().nullable(),
  kind: healthAccessKindSchema,
  reason: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type HealthAccessLog = z.infer<typeof healthAccessLogSchema>;

export const exportHealthSchema = z.object({
  /** Exportar ficha de saúde exige justificativa registrada (seção 8.1). */
  reason: z.string().trim().min(10, 'Diga por que precisa exportar').max(300),
});
export type ExportHealthInput = z.infer<typeof exportHealthSchema>;
